import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Chapter, Reveal } from "@/components/landing/Reveal";

const STEPS = [
  { name: "IDEA", desc: "A question, a hunch, a story worth telling.", tag: "Input" },
  { name: "RESEARCH", desc: "Agents gather and read hundreds of sources.", tag: "Discovery" },
  { name: "VERIFIED SOURCES", desc: "Evidence is organized, cross-checked, and cited.", tag: "Validation" },
  { name: "SCRIPT", desc: "Evidence becomes a structured narrative arc.", tag: "Writing" },
  { name: "STORYBOARD", desc: "The script breaks into scenes, shots, and visual direction.", tag: "Planning" },
  { name: "NARRATION", desc: "Natural voice performance rendered from the script.", tag: "Audio" },
  { name: "VISUALS", desc: "Footage, archive, and generated frames for every scene.", tag: "Media" },
  { name: "VIDEO", desc: "Assembly — timing, music, captions, final cut.", tag: "Output" },
];

const PipelineStep = ({ step, index }) => (
  <motion.div
    initial={{ opacity: 0.25 }}
    whileInView={{ opacity: 1 }}
    viewport={{ margin: "-42% 0px -42% 0px" }}
    transition={{ duration: 0.6 }}
    className="group flex gap-6 sm:gap-10"
    data-testid={`pipeline-step-${index + 1}`}
  >
    <motion.div
      initial={{ borderColor: "rgba(255,255,255,0.12)", color: "#666666", boxShadow: "0 0 0 rgba(212,175,55,0)" }}
      whileInView={{
        borderColor: "rgba(212,175,55,0.7)",
        color: "#D4AF37",
        boxShadow: "0 0 22px rgba(212,175,55,0.28)",
      }}
      viewport={{ margin: "-42% 0px -42% 0px" }}
      transition={{ duration: 0.6 }}
      className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-[#090909] font-mono text-[11px]"
    >
      {String(index + 1).padStart(2, "0")}
    </motion.div>
    <div className="pb-14 pt-1.5">
      <motion.h3
        initial={{ color: "#555555" }}
        whileInView={{ color: "#F5F5F5" }}
        viewport={{ margin: "-42% 0px -42% 0px" }}
        transition={{ duration: 0.6 }}
        className="font-display text-xl font-bold tracking-wide sm:text-2xl"
      >
        {step.name}
      </motion.h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-[#9A9A9A]">{step.desc}</p>
      <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#555555]">
        Stage {String(index + 1).padStart(2, "0")} — {step.tag}
      </div>
    </div>
  </motion.div>
);

export default function Pipeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.72", "end 0.55"],
  });
  const scaleY = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section id="how-it-works" className="relative py-28 sm:py-36" data-testid="pipeline-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Chapter index="03" label="How It Works" testid="pipeline-chapter" />

        <Reveal>
          <h2
            className="max-w-3xl font-display text-3xl font-bold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl"
            data-testid="pipeline-section-title"
          >
            One story. <span className="text-[#9A9A9A]">One production pipeline.</span>
          </h2>
        </Reveal>

        <div ref={ref} className="relative mx-auto mt-20 max-w-3xl">
          <div className="absolute bottom-8 left-[21px] top-2 w-px bg-white/8" aria-hidden="true" />
          <motion.div
            className="absolute left-[21px] top-2 w-px origin-top bg-gradient-to-b from-[#D4AF37] to-[#D4AF37]/30"
            style={{ scaleY, height: "calc(100% - 40px)" }}
            aria-hidden="true"
          />
          {STEPS.map((step, i) => (
            <PipelineStep key={step.name} step={step} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
